import {
    Box,
    Chip,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
    ThemeProvider,
    Tooltip,
    Typography,
    createTheme,
    useTheme,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
    ReactElement,
    SetStateAction,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { setFilterList } from '../../../redux/slices/entitySlice'
import {
    Applications,
    AttributesMetadata,
    IN_TRANSIT_TABS,
} from '../../../constants/constants'
import {
    deriveColumnFiltersFromStore,
    getEntityTableFilters,
    getEntityTableGlobalSearchTextFilter,
    getNonColumnLevelFilters,
} from '../../../utils/EntityUtils'
import {
    getAlarmStatusFilters,
    getShipmentStatusFilters,
} from '../../../utils/IntransitUtils'
import { IColumn, IDateFilter, ITableFilters } from '../../Entity/interfaces'
import { ColumnOrderListType } from '../../../redux/slices/entitySlice'
import ColumnFilterInput from '../../Entity/Components/ColumnFilterInput'
import TimeZoneDisplay from '../../Common/TimezoneDisplay'
import InTransitFilterRow from './InTransitFilterRow'

dayjs.extend(utc)

interface MapFullscreenFilterOverlayProps {
    templateId: string
    searchableColumns: ColumnOrderListType[]
    setSearchValue: React.Dispatch<SetStateAction<string>>
    setSelectedColumn: React.Dispatch<SetStateAction<IColumn | null>>
    dateFilterAttributeId: string
    dateFilterLabel: string
    totalCount?: number
    totalCountWithoutFilters?: number
}

const tabIndexString = IN_TRANSIT_TABS.MAP_VIEW.toString()
const applicationId = Applications.inTransit.id

/**
 * Lazily returns the active fullscreen element (or document.body as fallback).
 * Must be a function — a static reference captures null at theme-creation time,
 * before the browser has entered fullscreen.
 */
const getFullscreenContainer = () => document.fullscreenElement ?? document.body

const MapFullscreenFilterOverlay = ({
    templateId,
    searchableColumns,
    setSearchValue,
    setSelectedColumn,
    dateFilterAttributeId,
    dateFilterLabel,
    totalCount,
    totalCountWithoutFilters,
}: MapFullscreenFilterOverlayProps): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const baseTheme = useTheme()

    const allTableFilters = useAppSelector(
        (state) => state.entity.allTableFilters,
    )
    const columnVisibilityList = useAppSelector(
        (state) => state.entity.entityColumns,
    )
    const inTransitSettings = useAppSelector(
        (state) => state.inTransit.inTransitSettings,
    )

    const shipmentStatusAttributeId =
        inTransitSettings?.shipmentStatusAttributeId || 'shipmentStatus'
    const alarmStatusAttributeId =
        inTransitSettings?.alarmStatusAttributeId || 'alarmStatus'

    const allAttributesFilters = getEntityTableFilters(
        allTableFilters,
        templateId,
        applicationId,
        tabIndexString,
    )

    const gloalSearchText = getEntityTableGlobalSearchTextFilter(
        allTableFilters,
        templateId,
        applicationId,
        tabIndexString,
    )

    const columnFilters = useMemo(
        () =>
            deriveColumnFiltersFromStore(
                allAttributesFilters,
                columnVisibilityList,
            ),
        [allAttributesFilters, columnVisibilityList],
    )

    const fromDate =
        (allAttributesFilters[dateFilterAttributeId]?.startDate as string) || ''
    const toDate =
        (allAttributesFilters[dateFilterAttributeId]?.endDate as string) || ''
    const dateRange: [string | null, string | null] = useMemo(
        () => [fromDate || null, toDate || null],
        [fromDate, toDate],
    )

    const shipmentStatusFilter = getShipmentStatusFilters(
        allAttributesFilters,
        shipmentStatusAttributeId,
    )
    const alarmStatusFilter = getAlarmStatusFilters(
        allAttributesFilters,
        alarmStatusAttributeId,
    )
    const searchText = gloalSearchText?.searchText?.trim() || ''

    const activeColumnFilterCount = useMemo(
        () =>
            columnFilters.filter(
                (f) =>
                    f.value !== undefined &&
                    f.value !== '' &&
                    !(Array.isArray(f.value) && f.value.length === 0),
            ).length,
        [columnFilters],
    )

    const filtersCount =
        activeColumnFilterCount +
        (searchText ? 1 : 0) +
        (fromDate && toDate ? 1 : 0) +
        (shipmentStatusFilter ? 1 : 0) +
        (alarmStatusFilter ? 1 : 0)

    const [isPanelOpen, setIsPanelOpen] = useState(true)
    const [expandedColId, setExpandedColId] = useState<string | null>(null)
    const chipRefs = useRef<Record<string, HTMLSpanElement | null>>({})

    /**
     * Fullscreen dropdown fix — two-part strategy:
     *
     * 1. disablePortal: true  — render the Popper/Popover inline in the DOM tree
     *    (inside the fullscreen element) so it is actually visible. Without this,
     *    portals render into document.body which is outside the fullscreen clip.
     *
     * 2. popperOptions: { strategy: 'fixed' }  — force Popper.js to use
     *    position:fixed instead of its default position:absolute. In fullscreen
     *    mode the browser treats the fullscreen element as the viewport, so
     *    getBoundingClientRect() coordinates and position:fixed share exactly the
     *    same origin. position:fixed also takes the element out of flow, so large
     *    popovers (e.g. the DateRangePicker calendar) cause no layout shifts.
     *
     * MuiModal keeps the container redirect so any dialog-style overlays still
     * render inside the fullscreen element.
     */
    const fullscreenTheme = useMemo(
        () =>
            createTheme(baseTheme, {
                components: {
                    MuiModal: {
                        defaultProps: { container: getFullscreenContainer },
                    },
                    MuiPopper: {
                        defaultProps: {
                            disablePortal: true,
                            popperOptions: {
                                strategy: 'fixed',
                                // preventOverflow would shift the dropdown right to the
                                // edge of the nearest scrollable ancestor, creating a
                                // ~51px offset between the anchor and the listbox. The
                                // fullscreen overlay fills the whole viewport so there
                                // is nothing to overflow into — disable it.
                                modifiers: [
                                    { name: 'preventOverflow', enabled: false },
                                ],
                            },
                        },
                    },
                    MuiPopover: {
                        defaultProps: { disablePortal: true },
                    },
                    MuiAutocomplete: {
                        defaultProps: { disablePortal: true },
                    },
                    // MUI X DateRangePicker uses its own PickersPopper component
                    // (theme key: MuiPickersPopper), separate from MuiPopper.
                    MuiPickersPopper: {
                        defaultProps: {
                            disablePortal: true,
                            popperOptions: { strategy: 'fixed' },
                        },
                    },
                },
            }),
        [baseTheme],
    )

    const filterableColumns = useMemo(
        () =>
            columnVisibilityList.filter(
                (col) =>
                    col.selected &&
                    ((col as any).metadata?.[AttributesMetadata.FILTER] ||
                        col.columnName === 'Type'),
            ),
        [columnVisibilityList],
    )

    const triggerSearch = useCallback(
        (searchTextArg?: string) => {
            setSearchValue(searchTextArg ?? gloalSearchText?.searchText ?? '')
            setSelectedColumn(gloalSearchText?.column ?? null)
        },
        [gloalSearchText, setSearchValue, setSelectedColumn],
    )

    const updateDateRange = useCallback(
        (newDateRange: [string, string]) => {
            const updated = allTableFilters.map((filter) => {
                if (
                    filter.applicationId === applicationId &&
                    filter.tabIndex === tabIndexString &&
                    filter.entityTemplateId === templateId
                ) {
                    return {
                        ...filter,
                        allAttributesFilters: {
                            ...filter.allAttributesFilters,
                            [dateFilterAttributeId]: {
                                type: 'date',
                                isColumnLevelFilter: false,
                                startDate: newDateRange[0],
                                endDate: newDateRange[1],
                            } as IDateFilter,
                        },
                    } as ITableFilters
                }
                return filter
            })
            dispatch(setFilterList(updated))
        },
        [allTableFilters, templateId, dateFilterAttributeId, dispatch],
    )

    const setOneColumnFilter = useCallback(
        (colId: string, value: any) => {
            const updated = allTableFilters.map((filter) => {
                if (
                    filter.applicationId === applicationId &&
                    filter.tabIndex === tabIndexString &&
                    filter.entityTemplateId === templateId
                ) {
                    const nonColFilters = getNonColumnLevelFilters(filter)
                    const existingColFilters = Object.entries(
                        filter.allAttributesFilters || {},
                    )
                        .filter(([, v]) => (v as any).isColumnLevelFilter)
                        .reduce(
                            (acc, [k, v]) => ({ ...acc, [k]: v }),
                            {} as Record<string, any>,
                        )

                    const isEmpty =
                        value === undefined ||
                        value === '' ||
                        (Array.isArray(value) && value.length === 0)

                    const updatedColFilters = isEmpty
                        ? Object.fromEntries(
                              Object.entries(existingColFilters).filter(
                                  ([k]) => k !== colId,
                              ),
                          )
                        : {
                              ...existingColFilters,
                              [colId]: {
                                  type: 'entity',
                                  isColumnLevelFilter: true,
                                  value,
                              },
                          }

                    return {
                        ...filter,
                        allAttributesFilters: {
                            ...nonColFilters,
                            ...updatedColFilters,
                        },
                    } as ITableFilters
                }
                return filter
            })
            dispatch(setFilterList(updated))
        },
        [allTableFilters, templateId, dispatch],
    )

    const clearAllFilters = useCallback(() => {
        const updated = allTableFilters.map((filter) => {
            if (
                filter.applicationId === applicationId &&
                filter.tabIndex === tabIndexString &&
                filter.entityTemplateId === templateId
            ) {
                return {
                    ...filter,
                    allAttributesFilters: {},
                    gloalSearchText: {
                        ...filter.gloalSearchText,
                        searchText: '',
                    },
                }
            }
            return filter
        })
        dispatch(setFilterList(updated))
        setSearchValue('')
        setExpandedColId(null)
    }, [allTableFilters, templateId, dispatch, setSearchValue])

    const getColFilterValue = (colId: string) => {
        const found = columnFilters.find((f) => f.id === colId)
        return found?.value
    }

    const isColActive = (colId: string) => {
        const v = getColFilterValue(colId)
        return (
            v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
        )
    }

    return (
        <ThemeProvider theme={fullscreenTheme}>
            {/* Collapsed pill — visible when toolbar is hidden */}
            {!isPanelOpen && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 10,
                    }}
                >
                    <Box
                        onClick={() => setIsPanelOpen(true)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(255,255,255,0.96)',
                            border: '0.5px solid rgba(0,0,0,0.18)',
                            borderRadius: '99px',
                            padding: '6px 14px 6px 10px',
                            cursor: 'pointer',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.14)',
                            userSelect: 'none',
                            '&:hover': {
                                background: 'rgba(245,245,245,0.98)',
                            },
                        }}
                    >
                        <FilterListIcon
                            sx={{
                                fontSize: 18,
                                color: baseTheme.palette.primary.main,
                            }}
                        />
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                            {t('filters')}
                        </Typography>
                        {totalCount !== undefined && (
                            <Typography
                                sx={{ fontSize: 13, color: '#555', ml: '2px' }}
                            >
                                ({totalCount})
                            </Typography>
                        )}
                        {filtersCount > 0 && (
                            <Box
                                sx={{
                                    background: baseTheme.palette.primary.main,
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    borderRadius: '99px',
                                    padding: '1px 7px',
                                    lineHeight: '16px',
                                }}
                            >
                                {filtersCount}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {/* Expanded toolbar — same look as EntityList toolbar */}
            {isPanelOpen && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 10,
                    }}
                >
                    {/* Row 1: primary theme colour background — matches EntityList toolbar.
                        Collapse button is on the LEFT so it does not conflict with
                        MapControls' fullscreen toggle which sits at top-right (zIndex 999). */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            background: (baseTheme as any).tableHeader?.color,
                            // Right padding clears MapControls' fullscreen
                            // toggle button (40px wide, 10px from the right
                            // edge, zIndex 999) so it never overlaps content.
                            padding: '0px 56px 0px 4px',
                            gap: '8px',
                        }}
                    >
                        <Tooltip title={t('collapse')} placement="bottom">
                            <IconButton
                                size="small"
                                onClick={() => setIsPanelOpen(false)}
                                sx={{
                                    color: '#fff',
                                    opacity: 0.8,
                                    flexShrink: 0,
                                }}
                            >
                                <KeyboardArrowUpIcon />
                            </IconButton>
                        </Tooltip>

                        {/* flex: 1 + minWidth: 0 keeps InTransitFilterRow from
                            overflowing and pushing other items off-screen */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <InTransitFilterRow
                                templateId={templateId}
                                applicationId={applicationId}
                                tabIndex={tabIndexString}
                                triggerSearch={triggerSearch}
                                searchableColumns={searchableColumns}
                                dateRange={dateRange}
                                updateDateRange={updateDateRange}
                                dateFilterLabel={dateFilterLabel}
                                filtersCount={filtersCount}
                                clearAllFilters={clearAllFilters}
                                totalCount={totalCount}
                                totalCountWithoutFilters={
                                    totalCountWithoutFilters
                                }
                            />
                        </Box>

                        <Box sx={{ flexShrink: 0 }}>
                            <TimeZoneDisplay />
                        </Box>
                    </Box>

                    {/* Row 2: column filter chips */}
                    {filterableColumns.length > 0 && (
                        <Box
                            sx={{
                                background: '#fff',
                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                padding: '6px 20px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '6px',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: '#888',
                                        mr: '4px',
                                    }}
                                >
                                    {t('filterBy')}:
                                </Typography>
                                {filterableColumns.map((col) => {
                                    const active = isColActive(col.id)
                                    const isExpanded = expandedColId === col.id
                                    return (
                                        <span
                                            key={col.id}
                                            ref={(el) => {
                                                chipRefs.current[col.id] = el
                                            }}
                                        >
                                            <Chip
                                                label={t(col.columnName)}
                                                size="small"
                                                variant={
                                                    active || isExpanded
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                                color={
                                                    active || isExpanded
                                                        ? 'primary'
                                                        : 'default'
                                                }
                                                onClick={() =>
                                                    setExpandedColId(
                                                        isExpanded
                                                            ? null
                                                            : col.id,
                                                    )
                                                }
                                                onDelete={
                                                    active
                                                        ? () => {
                                                              setOneColumnFilter(
                                                                  col.id,
                                                                  undefined,
                                                              )
                                                              if (
                                                                  expandedColId ===
                                                                  col.id
                                                              ) {
                                                                  setExpandedColId(
                                                                      null,
                                                                  )
                                                              }
                                                          }
                                                        : undefined
                                                }
                                                sx={{
                                                    fontSize: '14px',
                                                    // The label's line-box otherwise inherits the
                                                    // body line-height, which is taller than the
                                                    // glyphs — flex centers the box, not the ink,
                                                    // so the text renders low. lineHeight: 1 fixed
                                                    // the offset but clipped descenders (g, p, y)
                                                    // against the label's overflow:hidden — 20px
                                                    // (chip height is 24px) leaves room for them.
                                                    lineHeight: '20px',
                                                }}
                                            />
                                        </span>
                                    )
                                })}
                            </Box>

                            {/* Filter input — floats as a popper anchored to the
                                expanded chip, so it never reserves layout space
                                (no extra row) and aligns directly under the chip
                                instead of an offset computed from offsetLeft. */}
                            <Popper
                                open={!!expandedColId}
                                anchorEl={
                                    expandedColId
                                        ? chipRefs.current[expandedColId]
                                        : null
                                }
                                placement="bottom-start"
                                sx={{ zIndex: 20 }}
                                modifiers={[
                                    {
                                        name: 'offset',
                                        options: { offset: [0, 4] },
                                    },
                                ]}
                            >
                                <ClickAwayListener
                                    onClickAway={() => setExpandedColId(null)}
                                >
                                    <Paper elevation={4} sx={{ width: 280 }}>
                                        {filterableColumns
                                            .filter(
                                                (col) =>
                                                    col.id === expandedColId,
                                            )
                                            .map((col) => (
                                                <ColumnFilterInput
                                                    key={col.id}
                                                    id={col.id}
                                                    header={t(col.columnName)}
                                                    dataType={
                                                        (col as any).dataType ||
                                                        col.dataType
                                                    }
                                                    range={
                                                        (col as any).range || ''
                                                    }
                                                    attributeName={
                                                        col.attributeName
                                                    }
                                                    value={getColFilterValue(
                                                        col.id,
                                                    )}
                                                    onChange={(val) =>
                                                        setOneColumnFilter(
                                                            col.id,
                                                            val,
                                                        )
                                                    }
                                                    templateId={templateId}
                                                />
                                            ))}
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </Box>
                    )}
                </Box>
            )}
        </ThemeProvider>
    )
}

export default MapFullscreenFilterOverlay
